import React, { useState } from "react";
import { useSession } from "../contexts/SessionContext";
import { useShop } from "../contexts/ShopContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Play, Square, Clock, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function SessionBar() {
  const { activeShopId, activeShop } = useShop();
  const { session, refresh } = useSession();
  const [openDlg, setOpenDlg] = useState(false);
  const [closeDlg, setCloseDlg] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [closeResult, setCloseResult] = useState(null);
  const [preview, setPreview] = useState(null);

  React.useEffect(() => {
    if (closeDlg && session && !closeResult) {
      api.get(`/sessions/${session.session_id}/summary`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    } else if (!closeDlg) {
      setPreview(null);
    }
  }, [closeDlg, session, closeResult]);

  const doOpen = async () => {
    try {
      await api.post("/sessions/open", { shop_id: activeShopId, opening_cash: Number(openingCash || 0) });
      toast.success("Session opened");
      setOpenDlg(false);
      setOpeningCash("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doClose = async () => {
    try {
      const r = await api.post(`/sessions/${session.session_id}/close`, { closing_cash: Number(closingCash || 0) });
      setCloseResult(r.data);
      toast.success("Session closed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!session) {
    return (
      <>
        <div className="bg-destructive/10 border-b border-destructive/30 px-4 py-2 flex items-center justify-between" data-testid="session-bar-closed">
          <div className="text-sm">
            <span className="font-heading font-bold text-destructive">No active session</span>
            <span className="ml-2 text-muted-foreground">— Open a session for {activeShop?.name} to start billing</span>
          </div>
          <Button size="sm" onClick={() => setOpenDlg(true)} data-testid="open-session-btn" className="bg-terracotta hover:bg-terracotta-hover text-white gap-1">
            <Play size={14}/> Open Session
          </Button>
        </div>
        <Dialog open={openDlg} onOpenChange={setOpenDlg}>
          <DialogContent data-testid="open-session-dialog">
            <DialogHeader><DialogTitle className="font-heading">Open Session — {activeShop?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Opening Cash in Drawer (₹)</Label>
                <Input type="number" data-testid="opening-cash-input" autoFocus value={openingCash} onChange={(e)=>setOpeningCash(e.target.value)} placeholder="0" />
                <p className="text-[11px] text-muted-foreground mt-1">Cash already in drawer at the start of the day. Used for end-of-day reconciliation.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpenDlg(false)}>Cancel</Button>
              <Button onClick={doOpen} data-testid="confirm-open-session" className="bg-terracotta hover:bg-terracotta-hover text-white">Open Session</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const opened = new Date(session.opened_at);
  return (
    <>
      <div className="bg-moss/10 border-b border-moss/30 px-4 py-2 flex items-center justify-between" data-testid="session-bar-open">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5"><Clock size={14} className="text-moss"/><span className="font-medium">Session #{session.session_id.slice(-6)}</span></div>
          <div className="flex items-center gap-1.5 text-muted-foreground"><Wallet size={14}/>Opening: <span className="font-mono text-ink">₹{session.opening_cash.toFixed(2)}</span></div>
          <div className="text-muted-foreground text-xs">Opened by {session.opened_by_name} at {opened.toLocaleTimeString()}</div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCloseDlg(true)} data-testid="close-session-btn" className="border-destructive/40 text-destructive hover:bg-destructive/10 gap-1">
          <Square size={14}/> Close Session
        </Button>
      </div>

      <Dialog open={closeDlg} onOpenChange={(o) => { setCloseDlg(o); if (!o) setCloseResult(null); }}>
        <DialogContent data-testid="close-session-dialog">
          <DialogHeader><DialogTitle className="font-heading">Close Session — End of Day</DialogTitle></DialogHeader>
          {!closeResult ? (
            <>
              {preview && (
                <div className="bg-sand p-3 rounded mb-3 text-sm">
                  <div className="font-heading font-bold mb-2">Today's Sales (this session)</div>
                  <div className="grid grid-cols-2 gap-y-1">
                    <div>Bills</div><div className="text-right font-mono">{preview.bill_count}</div>
                    <div>Cash sales</div><div className="text-right font-mono" data-testid="preview-cash">₹{preview.by_method.cash.toFixed(2)}</div>
                    <div>GPay</div><div className="text-right font-mono" data-testid="preview-gpay">₹{preview.by_method.gpay.toFixed(2)}</div>
                    <div>MPay</div><div className="text-right font-mono" data-testid="preview-mpay">₹{preview.by_method.mpay.toFixed(2)}</div>
                    <div>Card</div><div className="text-right font-mono" data-testid="preview-card">₹{preview.by_method.card.toFixed(2)}</div>
                    <div className="font-bold border-t border-border pt-1 mt-1">Total Sales</div>
                    <div className="text-right font-mono font-bold border-t border-border pt-1 mt-1">₹{preview.total_sales.toFixed(2)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-y-1 mt-2 border-t border-border pt-2">
                    <div>Opening cash</div><div className="text-right font-mono">₹{preview.opening_cash.toFixed(2)}</div>
                    <div className="font-bold">Expected cash in drawer</div>
                    <div className="text-right font-mono font-bold text-terracotta" data-testid="preview-expected">₹{preview.expected_cash.toFixed(2)}</div>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Closing Cash Counted in Drawer (₹)</Label>
                  <Input type="number" data-testid="closing-cash-input" autoFocus value={closingCash} onChange={(e)=>setClosingCash(e.target.value)} placeholder="0" />
                  <p className="text-[11px] text-muted-foreground mt-1">Count physical cash and enter exact amount. System will compare with expected cash.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setCloseDlg(false)}>Cancel</Button>
                <Button onClick={doClose} data-testid="confirm-close-session" className="bg-destructive hover:bg-destructive/90 text-white">Close Session</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2 bg-sand p-4 rounded">
                <div className="font-heading font-bold text-lg">Session Summary</div>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div>Total bills</div><div className="text-right font-mono">{closeResult.summary?.bill_count || 0}</div>
                  <div>Total Sales</div><div className="text-right font-mono">₹{(closeResult.summary?.total_sales || 0).toFixed(2)}</div>
                  <div>Cash sales</div><div className="text-right font-mono">₹{(closeResult.summary?.by_method?.cash || 0).toFixed(2)}</div>
                  <div>GPay</div><div className="text-right font-mono">₹{(closeResult.summary?.by_method?.gpay || 0).toFixed(2)}</div>
                  <div>MPay</div><div className="text-right font-mono">₹{(closeResult.summary?.by_method?.mpay || 0).toFixed(2)}</div>
                  <div>Card</div><div className="text-right font-mono">₹{(closeResult.summary?.by_method?.card || 0).toFixed(2)}</div>
                </div>
                <div className="border-t border-border pt-2 grid grid-cols-2 gap-y-1 text-sm">
                  <div>Opening cash</div><div className="text-right font-mono">₹{closeResult.opening_cash.toFixed(2)}</div>
                  <div>Expected cash</div><div className="text-right font-mono">₹{closeResult.expected_cash.toFixed(2)}</div>
                  <div>Counted cash</div><div className="text-right font-mono">₹{closeResult.closing_cash.toFixed(2)}</div>
                  <div className="font-bold">Difference</div>
                  <div className={`text-right font-mono font-bold ${closeResult.difference === 0 ? "text-moss" : closeResult.difference < 0 ? "text-destructive" : "text-ochre"}`}>
                    ₹{closeResult.difference.toFixed(2)} {closeResult.difference < 0 ? "(short)" : closeResult.difference > 0 ? "(over)" : "(perfect)"}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCloseDlg(false); setCloseResult(null); setClosingCash(""); }} className="bg-terracotta hover:bg-terracotta-hover text-white" data-testid="close-summary-done">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
